import { isSafeUrl } from "@/pages/agent-interfaces/live-agent/components/infoLinkIcons";
import type { InfoLink } from "@/types/agent/info-links";

export const RESOURCE_LABEL_MAX = 120;
export const RESOURCE_DESCRIPTION_MAX = 300;

export interface BulkLinkRow {
  label: string;
  url: string;
  description: string | null;
}

export interface BulkLinkRejection {
  raw: string;
  reason: string;
}

export interface BulkLinksParseResult {
  rows: BulkLinkRow[];
  rejected: BulkLinkRejection[];
}

export type BulkLinkStatus = "new" | "update" | "unchanged" | "over-limit";

export interface BulkLinksPlan {
  links: InfoLink[];
  entries: Array<{ row: BulkLinkRow; status: BulkLinkStatus }>;
  counts: Record<BulkLinkStatus, number>;
}

export const BULK_LINKS_FILE_EXTENSIONS = [".txt", ".md", ".csv", ".tsv", ".json"];
const BULK_LINKS_FILE_MAX_BYTES = 1024 * 1024;

const URL_PATTERN = /(https?:\/\/[^\s<>"'`]+|www\.[^\s<>"'`]+)/i;
const WHOLE_URL = /^(?:https?:\/\/|www\.)\S+$/i;
const TABLE_DELIMITERS = [",", "\t", ";"];
const HEADER_MAX_WORDS = 3;
const MARKDOWN_LINK = /\[([^\]]+)\]\(([^)\s]+)\)/;
const EDGE_SEPARATORS = /^[\s\-–—:|,;•*]+|[\s\-–—:|,;•*]+$/g;
const LIST_MARKER = /^\s*(?:[-*•]|\d+[.)])\s+/;

const LABEL_KEYS = ["label", "title", "name", "text"];
const URL_KEYS = ["url", "href", "link", "website"];
const DESCRIPTION_KEYS = ["description", "desc", "summary", "notes", "note", "tags"];
const LIST_KEYS = ["links", "resources", "items", "data"];

const clip = (value: string, max: number): string => (value.length > max ? value.slice(0, max).trimEnd() : value);

const cleanText = (value: string): string => value.replace(/\s+/g, " ").replace(EDGE_SEPARATORS, "").trim();

const normaliseUrl = (raw: string): string | null => {
  const trimmed = raw.trim().replace(/[.,;:!?)\]]+$/, "");
  const withScheme = /^www\./i.test(trimmed) ? `https://${trimmed}` : trimmed;
  return isSafeUrl(withScheme) ? withScheme : null;
};

/** Hostname + path, used as a readable fallback when a row has no label. */
const labelFromUrl = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    const path = parsed.pathname.replace(/\/$/, "");
    return `${parsed.hostname.replace(/^www\./, "")}${path}`;
  } catch {
    return url;
  }
};

/** Identity used to spot the same link across a paste and the existing list. */
export const linkUrlKey = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    const path = parsed.pathname.replace(/\/+$/, "");
    return `${parsed.host.toLowerCase().replace(/^www\./, "")}${path}${parsed.search}`;
  } catch {
    return url.trim().toLowerCase();
  }
};

const pickString = (record: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
    if (Array.isArray(value) && value.every((item) => typeof item === "string") && value.length > 0) {
      return value.join(", ");
    }
  }
  return null;
};

const buildRow = (label: string | null, url: string, description: string | null): BulkLinkRow => {
  const cleanLabel = label ? cleanText(label) : "";
  const cleanDescription = description ? cleanText(description) : "";
  return {
    label: clip(cleanLabel || labelFromUrl(url), RESOURCE_LABEL_MAX),
    url,
    description: cleanDescription ? clip(cleanDescription, RESOURCE_DESCRIPTION_MAX) : null,
  };
};

const parseLine = (line: string): BulkLinkRow | BulkLinkRejection | null => {
  const text = line.replace(LIST_MARKER, "").trim();
  if (!text) return null;

  const markdown = text.match(MARKDOWN_LINK);
  if (markdown) {
    const url = normaliseUrl(markdown[2]);
    if (!url) return { raw: line.trim(), reason: "Invalid URL" };
    const rest = `${text.slice(0, markdown.index)} ${text.slice((markdown.index ?? 0) + markdown[0].length)}`;
    return buildRow(markdown[1], url, rest);
  }

  const match = text.match(URL_PATTERN);
  if (!match) return { raw: line.trim(), reason: "No URL found" };
  const url = normaliseUrl(match[0]);
  if (!url) return { raw: line.trim(), reason: "Invalid URL" };

  const before = text.slice(0, match.index);
  const after = text.slice((match.index ?? 0) + match[0].length);
  return buildRow(before, url, after);
};

const parseJsonItem = (item: unknown): BulkLinkRow | BulkLinkRejection | null => {
  if (typeof item === "string") return parseLine(item);
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    return { raw: JSON.stringify(item), reason: "Unrecognised entry" };
  }
  const record = item as Record<string, unknown>;
  const rawUrl = pickString(record, URL_KEYS);
  if (!rawUrl) return { raw: JSON.stringify(item), reason: "No URL found" };
  const url = normaliseUrl(rawUrl);
  if (!url) return { raw: rawUrl, reason: "Invalid URL" };
  return buildRow(pickString(record, LABEL_KEYS), url, pickString(record, DESCRIPTION_KEYS));
};

const extractJson = (text: string): unknown => {
  const candidates = [text];
  const firstBracket = text.search(/[[{]/);
  const lastBracket = Math.max(text.lastIndexOf("]"), text.lastIndexOf("}"));
  if (firstBracket >= 0 && lastBracket > firstBracket) candidates.push(text.slice(firstBracket, lastBracket + 1));

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch {
      continue;
    }
  }
  return undefined;
};

const toJsonItems = (value: unknown): unknown[] | null => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  for (const key of LIST_KEYS) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }
  return pickString(record, URL_KEYS) ? [record] : null;
};

const parseDelimited = (text: string, delimiter: string): string[][] => {
  const records: string[][] = [];
  let record: string[] = [];
  let cell = "";
  let quoted = false;

  const endRecord = () => {
    record.push(cell);
    records.push(record);
    record = [];
    cell = "";
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (quoted) {
      if (char !== '"') cell += char;
      else if (text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else quoted = false;
    } else if (char === '"' && !cell.trim()) {
      quoted = true;
      cell = "";
    } else if (char === delimiter) {
      record.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i += 1;
      endRecord();
    } else cell += char;
  }
  endRecord();

  return records.filter((cells) => cells.some((value) => value.trim()));
};

const findColumn = (header: string[], keys: string[], taken: number[]): number => {
  const free = (index: number) => !taken.includes(index);
  const exact = header.findIndex((name, index) => free(index) && keys.includes(name));
  if (exact >= 0) return exact;
  return header.findIndex((name, index) => {
    const words = name.split(/[\s_-]+/);
    return free(index) && words.length <= HEADER_MAX_WORDS && words.some((word) => keys.includes(word));
  });
};

/**
 * Reads a spreadsheet export (CSV, TSV or semicolon-separated). Columns are
 * matched by header name; without a header row, the column holding URLs is
 * the link and the remaining columns are label then description.
 */
const parseTable = (text: string): Array<BulkLinkRow | BulkLinkRejection> | null => {
  const firstLine = text.split(/\r?\n/, 1)[0];
  const delimiter = TABLE_DELIMITERS.map((value) => ({ value, count: firstLine.split(value).length - 1 })).sort(
    (a, b) => b.count - a.count,
  )[0];
  if (delimiter.count === 0) return null;

  const records = parseDelimited(text, delimiter.value);
  const first = records[0] ?? [];
  const header = first.map((name) => name.trim().toLowerCase());

  let urlIndex = first.some((value) => URL_PATTERN.test(value)) ? -1 : findColumn(header, URL_KEYS, []);
  let labelIndex: number;
  let descriptionIndex: number;
  let rows = records;

  if (urlIndex >= 0) {
    labelIndex = findColumn(header, LABEL_KEYS, [urlIndex]);
    descriptionIndex = findColumn(header, DESCRIPTION_KEYS, [urlIndex, labelIndex]);
    rows = records.slice(1);
  } else {
    urlIndex = first.findIndex((value) => WHOLE_URL.test(value.trim()));
    if (urlIndex < 0) return null;
    const others = first.map((_, index) => index).filter((index) => index !== urlIndex);
    labelIndex = others[0] ?? -1;
    descriptionIndex = others[1] ?? -1;
  }

  return rows.map((cells) => {
    const rawUrl = cells[urlIndex]?.trim();
    if (!rawUrl) return { raw: cells.join(", "), reason: "No URL found" };
    const url = normaliseUrl(rawUrl);
    if (!url) return { raw: rawUrl, reason: "Invalid URL" };
    return buildRow(cells[labelIndex] ?? null, url, cells[descriptionIndex] ?? null);
  });
};

/** Reads a dropped or picked file of links as text, refusing formats the parser can't read. */
export const readBulkLinksFile = async (file: File): Promise<string> => {
  const extension = file.name.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] ?? "";
  if (!BULK_LINKS_FILE_EXTENSIONS.includes(extension)) {
    throw new Error(`${file.name} isn't supported. Use a ${BULK_LINKS_FILE_EXTENSIONS.join(", ")} file.`);
  }
  if (file.size > BULK_LINKS_FILE_MAX_BYTES) throw new Error(`${file.name} is too large. Keep it under 1 MB.`);
  return file.text();
};

/**
 * Turns whatever an owner pastes or uploads — AI-generated JSON (fenced or
 * not), a spreadsheet export, a markdown list, or plain "Label - https://…"
 * lines — into link rows.
 */
export const parseBulkLinks = (input: string): BulkLinksParseResult => {
  const fenced = input.match(/```(?:json|javascript|js)?\s*([\s\S]*?)```/i);
  const text = (fenced ? fenced[1] : input).replace(/^﻿/, "").trim();
  const rows: BulkLinkRow[] = [];
  const rejected: BulkLinkRejection[] = [];
  if (!text) return { rows, rejected };

  const looksLikeJson = /^[[{]/.test(text) && !/^\[[^\]]*\]\(/.test(text);
  const jsonItems = looksLikeJson || fenced ? toJsonItems(extractJson(text)) : null;
  if (looksLikeJson && !jsonItems) {
    return { rows, rejected: [{ raw: clip(text, 80), reason: "Couldn't read this JSON — check it was copied in full" }] };
  }
  const results = jsonItems
    ? jsonItems.map(parseJsonItem)
    : (parseTable(text) ?? text.split(/\r?\n/).map(parseLine));

  for (const result of results) {
    if (!result) continue;
    if ("reason" in result) rejected.push(result);
    else rows.push(result);
  }
  return { rows, rejected };
};

/**
 * Merges parsed rows into the existing links. A row whose URL is already
 * listed updates that link's label (and description, when the row has one)
 * in place; later duplicates within the same paste win. New links beyond
 * `maxLinks` are left out and reported as over the limit.
 */
export const planBulkLinks = (existing: InfoLink[], rows: BulkLinkRow[], maxLinks: number): BulkLinksPlan => {
  const deduped = new Map<string, BulkLinkRow>();
  for (const row of rows) deduped.set(linkUrlKey(row.url), row);

  const links = [...existing];
  const indexByKey = new Map(existing.map((link, index) => [linkUrlKey(link.url), index]));
  const entries: BulkLinksPlan["entries"] = [];
  const counts: Record<BulkLinkStatus, number> = { new: 0, update: 0, unchanged: 0, "over-limit": 0 };
  const idBase = Date.now();

  for (const [key, row] of deduped) {
    const index = indexByKey.get(key);
    let status: BulkLinkStatus;

    if (index !== undefined) {
      const current = links[index];
      const description = row.description ?? current.description ?? null;
      const changed = current.label !== row.label || (current.description ?? null) !== description;
      if (changed) {
        links[index] = {
          ...current,
          label: row.label,
          description,
          descriptionSource: row.description ? "owner" : current.descriptionSource,
        };
      }
      status = changed ? "update" : "unchanged";
    } else if (links.length < maxLinks) {
      links.push({
        id: `link-${idBase}-${entries.length}`,
        label: row.label,
        url: row.url,
        description: row.description,
        ...(row.description ? { descriptionSource: "owner" as const } : {}),
      });
      indexByKey.set(key, links.length - 1);
      status = "new";
    } else {
      status = "over-limit";
    }

    counts[status] += 1;
    entries.push({ row, status });
  }

  return { links, entries, counts };
};

export const buildBulkLinksPrompt = (genieName?: string | null, website?: string | null): string =>
  [
    `I'm setting up the resource links${genieName ? ` for "${genieName}"` : ""}, an AI assistant that talks to my customers.`,
    `List the most useful pages${website ? ` on ${website}` : " on my website"} a customer might need during a conversation`,
    "(pricing, booking, manuals, contact, policies, product pages, etc.).",
    "",
    "Reply with ONLY a JSON array, no commentary, in exactly this shape:",
    '[{ "label": "Short name shown on the button", "url": "https://…", "description": "What the page covers and when a customer would need it" }]',
    "",
    `Keep labels under ${RESOURCE_LABEL_MAX} characters and descriptions under ${RESOURCE_DESCRIPTION_MAX} characters.`,
  ].join("\n");
